import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from '../../authConfig.js';
import { adminGetUsers, adminUpsertUser } from '../../api.js';
import styles from './AdminTable.module.css';
import permStyles from './Permissions.module.css';

const EMPTY_FORM = { email: '', displayName: '', isAdmin: false, active: true };

export default function Users() {
  const { instance, accounts } = useMsal();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const getToken = useCallback(async () => {
    const resp = await instance.acquireTokenSilent({ ...apiRequest, account: accounts[0] });
    return resp.idToken;
  }, [instance, accounts]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const data = await adminGetUsers(token);
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  async function toggleField(user, field) {
    setSaving(user.userId);
    try {
      const token = await getToken();
      await adminUpsertUser(token, { ...user, [field]: !user[field] });
      await fetchUsers();
    } catch (err) {
      alert(`Erro: ${err.message}`);
    } finally {
      setSaving(null);
    }
  }

  async function handleAddUser(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = await getToken();
      // Use email as userId placeholder — system links the real OID on first login
      await adminUpsertUser(token, { userId: form.email, ...form });
      await fetchUsers();
      setShowModal(false);
      setForm(EMPTY_FORM);
    } catch (err) {
      alert(`Erro: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className={styles.loading}>A carregar utilizadores...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>Utilizadores</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className={styles.addBtn} onClick={() => setShowModal(true)}>
            + Novo utilizador
          </button>
          <button className={styles.refreshBtn} onClick={fetchUsers}>
            Actualizar
          </button>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Admin</th>
              <th>Activo</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.userId} className={saving === user.userId ? styles.saving : ''}>
                <td>{user.displayName || '—'}</td>
                <td>{user.email}</td>
                <td>
                  <button
                    className={`${styles.toggle} ${user.isAdmin ? styles.on : styles.off}`}
                    onClick={() => toggleField(user, 'isAdmin')}
                    disabled={saving === user.userId}
                    title={user.isAdmin ? 'Remover admin' : 'Tornar admin'}
                  >
                    {user.isAdmin ? 'Sim' : 'Não'}
                  </button>
                </td>
                <td>
                  <button
                    className={`${styles.toggle} ${user.active ? styles.on : styles.off}`}
                    onClick={() => toggleField(user, 'active')}
                    disabled={saving === user.userId}
                    title={user.active ? 'Desactivar' : 'Activar'}
                  >
                    {user.active ? 'Activo' : 'Inactivo'}
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={4} className={styles.emptyRow}>
                  Nenhum utilizador encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className={permStyles.overlay} onClick={() => setShowModal(false)}>
          <div className={permStyles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={permStyles.modalTitle}>Novo utilizador</h2>
            <form onSubmit={handleAddUser} className={permStyles.form}>
              <label>
                Email *
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="utilizador@empresa.com"
                />
              </label>
              <label>
                Nome de apresentação
                <input
                  value={form.displayName}
                  onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                  placeholder="Ex: João Silva"
                />
              </label>
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={form.isAdmin}
                  onChange={(e) => setForm((f) => ({ ...f, isAdmin: e.target.checked }))}
                />
                Administrador
              </label>
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                Conta activa
              </label>
              <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>
                O utilizador será automaticamente vinculado na primeira sessão.
              </p>
              <div className={permStyles.modalActions}>
                <button
                  type="button"
                  className={permStyles.cancelBtn}
                  onClick={() => { setShowModal(false); setForm(EMPTY_FORM); }}
                >
                  Cancelar
                </button>
                <button type="submit" className={permStyles.saveBtn} disabled={submitting}>
                  {submitting ? 'A guardar...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
